import { useCallback, useEffect, useState } from 'react';
import {
  FormDataConsumer,
  RadioButtonGroupInput,
  ReferenceInput,
  SelectInput,
  TextInput,
  required,
  useEditContext,
} from 'react-admin';

import { NUMBER_OF_ROWS_FOR_TEXTAREA } from '@app/constants';
import { useFormLanguage } from '@app/hooks/useFormLanguage';
import { DEFAULT_LANGUAGE } from '@app/utils/languages';
import AddIcon from '@mui/icons-material/Add';
import { Button, Grid, Typography } from '@mui/material';

import { DialogAddMedia } from '@app/components/DialogAddMedia';
import { LabelNotClickableBoolInput } from '@app/components/LabelNotClickableBoolInput';
import { RailsCarousel } from '@app/components/RailsCarousel';
import { ImageSelector } from '@app/components/common/ImageSelector';
import { ASPECT_RATIO_TYPES } from '@app/components/common/ImageSelector/ImageSelector';
import { LanguageSelector } from '@app/components/common/LanguageSelector';
import { MultiLingualField } from '@app/components/common/MultiLingualField';
import { GenericObject } from '@app/components/common/Table/DefaultTable/types';

import { CTA_TARGET_TYPE } from '..';

interface ExpertFormProps {
  title: string;
  ctaLogo: File | null | undefined;
  selectedMedia: GenericObject[];
  moveCard(dragIndex: number, hoverIndex: number): void;
  handleChangeSelectedMedia(selected: GenericObject[]): void;
  handleChangeCtaLogo(logo: File): void;
}

export const CTAForm = ({
  ctaLogo,
  title,
  selectedMedia,
  moveCard,
  handleChangeSelectedMedia,
  handleChangeCtaLogo,
}: ExpertFormProps) => {
  const { language, handleChangeLanguage } = useFormLanguage();

  const [isAddMediaOpen, setIsAddMediaOpen] = useState(false);
  const [isMediaDefined, setIsMediaDefined] = useState(false);
  const { refetch } = useEditContext();

  const handleCloseAddMedia = () => {
    setIsAddMediaOpen(false);
  };

  const deleteCard = (id: number) => {
    handleChangeSelectedMedia(selectedMedia.filter((item: GenericObject) => item.id !== id));
  };

  const updateRecord = useCallback(async () => {
    if (refetch && !isMediaDefined) {
      const response = await refetch();
      handleChangeSelectedMedia(response?.data?.vod ? [response?.data.vod] : []);
      setIsMediaDefined(true);
    }
  }, [refetch, isMediaDefined, handleChangeSelectedMedia]);

  useEffect(() => {
    updateRecord();
  }, [updateRecord]);

  return (
    <FormDataConsumer>
      {({ formData }) => (
        <div>
          <Typography data-cy={`edit-${title}`} variant="h3" sx={{ marginBottom: 2 }}>
            {title}
          </Typography>
          <LanguageSelector language={language} handleChangeLanguage={handleChangeLanguage} />

          <Grid spacing={2} xl={8} lg={8} md={12} xs={12} container item>
            <Grid item xs={12}>
              <ImageSelector
                aspectRatio={ASPECT_RATIO_TYPES.THUMBNAIL}
                getImageId={(record: GenericObject) => record?.cta_image_file_base_id as string}
                getImagePath={(record: GenericObject) => (record?.cta_image_file_base as GenericObject)?.path as string}
                image={ctaLogo}
                handleChangeImage={handleChangeCtaLogo}
              />
            </Grid>

            <Grid item xs={12}>
              <MultiLingualField
                variant="outlined"
                helperText="Maximum 30 characters"
                inputProps={{ maxLength: 30 }}
                validate={[required()]}
                label="Name"
                source="name"
                fullWidth
                language={language}
                Component={TextInput}
              />
            </Grid>
            <Grid item xs={12}>
              <MultiLingualField
                Component={TextInput}
                language={language}
                variant="outlined"
                helperText="Maximum 80 characters"
                inputProps={{ maxLength: 80 }}
                label="Heading"
                source="heading"
                validate={[required()]}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <MultiLingualField
                Component={TextInput}
                language={language}
                variant="outlined"
                label="Sub Heading"
                helperText="Maximum 25 characters"
                inputProps={{ maxLength: 25 }}
                source="sub_heading"
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <MultiLingualField
                helperText="Maximum 25 characters"
                inputProps={{ maxLength: 25 }}
                Component={TextInput}
                language={language}
                variant="outlined"
                label="Subject"
                source="subject"
                validate={[required()]}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <MultiLingualField
                Component={TextInput}
                language={language}
                variant="outlined"
                label="Description"
                multiline
                helperText="Maximum 155 characters"
                inputProps={{ maxLength: 155 }}
                rows={NUMBER_OF_ROWS_FOR_TEXTAREA}
                source="description"
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <ReferenceInput source="related_topic_id" reference="taxonomies">
                <SelectInput
                  variant="outlined"
                  helperText={false}
                  label="Topic"
                  fullWidth
                  optionText={(record) => record.category_name[DEFAULT_LANGUAGE]}
                />
              </ReferenceInput>
            </Grid>
            <Grid item xs={12}>
              <RadioButtonGroupInput
                label="Target"
                helperText={false}
                source="target_type"
                validate={[required()]}
                choices={[
                  { id: CTA_TARGET_TYPE.EXTERNAL_LINK, name: 'External URL' },
                  { id: CTA_TARGET_TYPE.MEDIA, name: 'Media' },
                ]}
              />
            </Grid>
            {formData.target_type === CTA_TARGET_TYPE.MEDIA && (
              <>
                <Grid item xs={12}>
                  <Button variant="contained" onClick={() => setIsAddMediaOpen(true)} startIcon={<AddIcon />}>
                    ADD MEDIA
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <RailsCarousel selectOnlyOne deleteCard={deleteCard} selected={selectedMedia} moveCard={moveCard} />
                </Grid>
              </>
            )}
            {formData.target_type === CTA_TARGET_TYPE.EXTERNAL_LINK && (
              <Grid item xs={12}>
                <TextInput
                  validate={[required()]}
                  variant="outlined"
                  helperText={false}
                  label="URL"
                  source="external_link_url"
                  fullWidth
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <LabelNotClickableBoolInput label="Published" source="is_published" />
            </Grid>
            <DialogAddMedia
              open={isAddMediaOpen}
              handleClose={handleCloseAddMedia}
              selected={selectedMedia}
              selectOnlyOne
              handleChangeSelected={handleChangeSelectedMedia}
            />
          </Grid>
        </div>
      )}
    </FormDataConsumer>
  );
};
